import { useCallback, useEffect, useState } from "react";
import {
  ApiError,
  api,
  type CycleResponse,
  type OperatorEntryRequest,
  type OperatorEntryStatus,
} from "../api/client";

/**
 * Entrée opérateur « Acheter maintenant » (2026-10-22).
 *
 * <p>Polle {@code GET /api/cycles/operator-entry} (5 s) tant que {@code enabled} (AUTO armé), et
 * resserre à 1 s pendant qu'une demande est PENDING/CONSUMED pour afficher l'issue (FILLED / REFUSED)
 * dès le tick suivant. {@code request()} pose la demande (cycle actif) ou ouvre un cycle BUY (hors
 * cycle) ; les 409 remontent en {@link OperatorEntryError} (code + motif humanisable).
 */

export class OperatorEntryError extends Error {
  readonly code: string;
  readonly reason: string;
  constructor(code: string, reason: string) {
    super(`${code}: ${reason}`);
    this.code = code;
    this.reason = reason;
  }
}

export interface OperatorEntryState {
  status: OperatorEntryStatus | null;
  pending: boolean;
  /** Pose la demande / ouvre le cycle. Retourne la demande (cycle actif) ou le cycle ouvert. */
  request: () => Promise<OperatorEntryRequest | CycleResponse>;
  cancel: () => Promise<void>;
  refresh: () => Promise<void>;
}

const IDLE_POLL_MS = 5_000;
const BUSY_POLL_MS = 1_000;

export function useOperatorEntry(enabled: boolean): OperatorEntryState {
  const [status, setStatus] = useState<OperatorEntryStatus | null>(null);
  const [pending, setPending] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const s = await api.operatorEntryStatus();
      setStatus(s);
    } catch {
      // Silencieux — la bannière d'état système porte l'injoignabilité backend.
    }
  }, []);

  const busy =
    status?.request?.status === "PENDING" || status?.request?.status === "CONSUMED";

  useEffect(() => {
    if (!enabled) {
      setStatus(null);
      return;
    }
    let cancelled = false;
    const tick = async () => {
      try {
        const s = await api.operatorEntryStatus();
        if (!cancelled) setStatus(s);
      } catch {
        // idem
      }
    };
    void tick();
    const id = setInterval(tick, busy ? BUSY_POLL_MS : IDLE_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [enabled, busy]);

  const request = useCallback(async (): Promise<OperatorEntryRequest | CycleResponse> => {
    setPending(true);
    try {
      const r = await api.requestOperatorEntry();
      await refresh();
      if (r && typeof r === "object" && "request" in r) return r.request;
      return r as CycleResponse;
    } catch (e) {
      if (e instanceof ApiError && typeof e.body === "object" && e.body !== null) {
        const b = e.body as { error?: string; reason?: string };
        throw new OperatorEntryError(b.error ?? "UNKNOWN", b.reason ?? "Refus sans motif.");
      }
      throw e;
    } finally {
      setPending(false);
    }
  }, [refresh]);

  const cancel = useCallback(async () => {
    setPending(true);
    try {
      await api.cancelOperatorEntry();
      await refresh();
    } finally {
      setPending(false);
    }
  }, [refresh]);

  return { status, pending, request, cancel, refresh };
}
